<script setup lang="ts">
/**
 * SampleViewerModal — 리포트 샘플 전체화면 뷰어.
 *
 * ## 왜 전체화면인가
 *
 * 이전에는 랜딩 안에 인라인 뷰어를 두고 2단(페이지당 340px)으로 보여줬는데, A4 를 340px 에
 * 욱여넣으면 본문 글씨가 3~4px 상당이 되어 읽을 수 없었다. 사용자에게 매번 확대를 요구하는
 * 대신, 표지 카드를 클릭하면 화면 전체를 써서 **처음부터 읽히는 크기**로 띄운다.
 *
 * ## 기능을 최소로 두는 이유
 *
 * 이 뷰어의 목적은 "구매 전 내용 확인" 하나뿐이다. 1단/2단 토글·줌 배율 같은 문서 도구는
 * 판단에 보태는 게 없으면서 조작 부담만 늘려 **의도적으로 넣지 않는다**.
 * 남긴 조작은 페이지 넘기기(버튼·키보드·스와이프)와 닫기뿐이다.
 *
 * 배경을 어둡게 두는 것도 같은 이유 — 흰 지면이 도드라져 읽기 편해진다(문서 뷰어 표준).
 *
 * 접근성(focus trap·ESC·배경 스크롤 잠금·aria-modal)은 reka-ui Dialog 가 처리한다.
 *
 * ## 소스·이벤트 일반화 (041)
 *
 * 탐색 "리포트 구성 미리보기" 섹션이 같은 뷰어로 정적 발췌본 PDF 를 열 수 있도록
 * 소스(대학 스트림 | 정적 URL)와 열람 이벤트를 파라미터화했다. **기본값은 현행
 * (`SAMPLE_VIEW` + `sample.materialId`)** — 랜딩 호출부(`SampleGallery`)는 무수정으로
 * 기존과 동일하게 동작한다(research R2·SC-005).
 */
import { computed, ref, watch } from 'vue';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-vue-next';
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui';

import { postEvent, sampleStreamUrl, type SampleMeta, type ViewEventType } from '#/api/landing';
import { usePdfViewer } from '#/composables/usePdfViewer';
import { trackViewSample } from '#/lib/analytics';

interface Props {
  /** 열림 상태(부모 v-model). */
  open: boolean;
  /** 표시할 대학 샘플(스트림 모드). 닫힌 상태·정적 URL 모드에서는 null. */
  sample: SampleMeta | null;
  /** 정적 PDF URL(정적 URL 모드) — sample 이 null 일 때만 사용한다. */
  staticUrl?: string | null;
  /** 정적 URL 모드의 상단 제목. */
  staticTitle?: string;
  /**
   * 정적 URL 모드의 하단 안내 문구 — 기본값은 탐색 "구성 미리보기"용(041). 자료실 전체 열람(050)처럼
   * 발췌본이 아닌 소스는 이 문구를 덮어쓴다(빈 문자열이면 안내 자체를 감춘다).
   */
  staticNote?: string;
  /**
   * 뷰어 열람 시 발화할 이벤트 유형. `'none'` 은 미발화(억제) —
   * 신설 유형 연결 전의 호출부가 랜딩 `SAMPLE_VIEW` 집계를 오염시키지 않게 한다(FR-008).
   * materialId 는 sample 이 있을 때만 함께 전송된다.
   */
  viewEvent?: ViewEventType | 'none';
}

const props = withDefaults(defineProps<Props>(), {
  staticUrl: null,
  staticTitle: '',
  staticNote: '실제 판매 리포트에서 구성 확인용으로 발췌한 미리보기입니다.',
  viewEvent: 'SAMPLE_VIEW',
});

const emit = defineEmits<{
  /** 열림 상태 변경(v-model:open). */
  'update:open': [value: boolean];
}>();

/**
 * 렌더 폭 — 큰 화면 기준으로 넉넉히 잡는다.
 *
 * pdfjs 는 canvas 를 `targetW * devicePixelRatio` 픽셀로 그리므로, CSS 로 늘리는 것과 달리
 * 이 값을 키우면 실제로 선명해진다. 표시 크기는 아래 CSS(`max-height`)가 화면에 맞춰 줄인다.
 */
const RENDER_WIDTH = 1000;

const { pages, loading, error, totalPages, load, cleanup } = usePdfViewer();

/** 현재 보는 페이지(0-base). */
const index = ref(0);

const current = computed(() => pages.value[index.value] ?? null);
const canPrev = computed(() => index.value > 0);
const canNext = computed(() => index.value < pages.value.length - 1);

function prev(): void {
  if (canPrev.value) index.value -= 1;
}
function next(): void {
  if (canNext.value) index.value += 1;
}

/** 키보드 좌우로 넘기기 — Dialog 안이라 ESC 는 reka-ui 가 처리한다. */
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'ArrowLeft') prev();
  else if (e.key === 'ArrowRight') next();
}

/** 스와이프 판정 시작점(모바일). */
let touchStartX = 0;

function onTouchStart(e: TouchEvent): void {
  touchStartX = e.changedTouches[0]?.clientX ?? 0;
}

function onTouchEnd(e: TouchEvent): void {
  const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX;
  // 40px 미만은 탭·흔들림으로 보고 무시한다.
  if (Math.abs(dx) < 40) return;
  if (dx > 0) prev();
  else next();
}

/** 상단 제목 — 스트림 모드는 대학명, 정적 URL 모드는 staticTitle. */
const title = computed(() => props.sample?.universityName ?? props.staticTitle);

/**
 * 열릴 때만 PDF 를 로드하고, 닫히면 즉시 해제한다.
 *
 * 렌더 결과를 전 페이지 data URL 로 들고 있어 메모리를 꽤 차지한다 — 닫아둔 뷰어가 이를
 * 붙들고 있을 이유가 없으므로 cleanup 으로 문서·페이지를 모두 버린다.
 *
 * 소스 해석: sample(대학 스트림)이 우선, 없으면 staticUrl(정적 발췌본). 둘 다 없으면 로드 생략.
 * 이벤트는 로드 시작 시 1회 발화(research R8) — 'none' 이면 억제.
 */
watch(
  () => [props.open, props.sample?.universityId, props.staticUrl] as const,
  ([isOpen, universityId, staticUrl]) => {
    const url = universityId === undefined ? staticUrl : sampleStreamUrl(universityId);
    if (!isOpen || !url) {
      void cleanup();
      return;
    }
    index.value = 0;
    void load(url, 1, RENDER_WIDTH);
    if (props.viewEvent !== 'none') {
      void postEvent(props.viewEvent, props.sample?.materialId);
      // GA4 는 자체 적재(postEvent)를 대체하지 않고 병존한다 — 자체 DB 는 정산·운영 지표,
      // GA4 는 유입 분석. 대학명을 함께 남길 수 있는 지점이라 여기서만 보낸다.
      if (props.sample) {
        trackViewSample(
          props.sample.universityId,
          props.sample.universityName,
          props.sample.materialId,
        );
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <DialogRoot :open="open" @update:open="emit('update:open', $event)">
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-modal bg-overlay-deep data-[state=open]:animate-fo-fade"
      />

      <!-- 전체화면 — 기존 모달들과 달리 폭 제한을 두지 않는다(지면을 최대한 크게). -->
      <DialogContent
        class="fixed inset-0 z-modal flex flex-col focus:outline-none"
        @keydown="onKeydown"
      >
        <!-- 상단 바: 대학명 + 페이지 + 닫기 -->
        <div
          class="flex flex-none items-center justify-between gap-4 border-b border-white/10 px-4 py-3 md:px-6"
        >
          <DialogTitle class="m-0 truncate text-[15px] font-bold text-white md:text-[17px]">
            {{ title }}
            <span v-if="sample" class="ml-2 font-medium text-white/55">리포트 샘플</span>
          </DialogTitle>

          <div class="flex flex-none items-center gap-3">
            <span v-if="pages.length" class="text-[13px] font-semibold tabular-nums text-white/70">
              {{ index + 1 }} / {{ totalPages ?? pages.length }}
            </span>
            <DialogClose
              class="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-white/50 hover:bg-white/10"
              aria-label="닫기"
            >
              <X :size="18" :stroke-width="2" />
            </DialogClose>
          </div>
        </div>

        <!-- 지면 -->
        <div
          class="relative flex flex-1 items-center justify-center overflow-hidden p-3 md:p-6"
          @touchstart.passive="onTouchStart"
          @touchend.passive="onTouchEnd"
        >
          <div
            v-if="loading && !pages.length"
            class="flex flex-col items-center gap-3 text-white/70"
          >
            <Loader2 :size="28" class="animate-spin" />
            <p class="text-[13.5px]">리포트를 불러오는 중입니다</p>
          </div>

          <!-- 뷰어 실패는 대부분 브라우저 호환(구형 엔진) — 미보유 "준비 중" 과 구분되는 문구(066 FR-010) -->
          <p v-else-if="error" class="max-w-[28rem] text-center text-[13.5px] text-white/70">
            문서를 표시할 수 없습니다. 브라우저를 업데이트하거나 다른 브라우저로 열어 주세요.
          </p>

          <img
            v-else-if="current"
            :src="current.dataUrl"
            :alt="`${title} ${index + 1}페이지`"
            class="max-h-full max-w-full rounded-[4px] object-contain shadow-lg"
          />

          <!-- 좌우 넘기기 — 지면 위에 겹쳐 두어 세로 공간을 뺏지 않는다 -->
          <button
            v-if="canPrev"
            type="button"
            class="bg-white/12 absolute left-2 flex h-11 w-11 items-center justify-center rounded-full text-white backdrop-blur-sm transition-colors hover:bg-white/25 md:left-5"
            aria-label="이전 페이지"
            @click="prev"
          >
            <ChevronLeft :size="22" :stroke-width="2.2" />
          </button>
          <button
            v-if="canNext"
            type="button"
            class="bg-white/12 absolute right-2 flex h-11 w-11 items-center justify-center rounded-full text-white backdrop-blur-sm transition-colors hover:bg-white/25 md:right-5"
            aria-label="다음 페이지"
            @click="next"
          >
            <ChevronRight :size="22" :stroke-width="2.2" />
          </button>
        </div>

        <!-- 하단 안내 — 발췌본이라는 사실과 완본 분량을 밝힌다(모드별 문구, 정적 모드는 staticNote 오버라이드 가능) -->
        <p
          v-if="sample || staticNote"
          class="flex-none px-4 pb-4 text-center text-[12.5px] text-white/50 md:pb-5"
        >
          {{
            sample
              ? '실제 리포트는 대학별 12~16페이지 분량입니다. 위는 일부를 발췌한 샘플입니다.'
              : staticNote
          }}
        </p>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
